/**
 * 可复现的滚动基准（scroll benchmark）。
 *
 * 这是 task-3 验收「1 万行滚动 60fps」的**测量方法本身**，而不是一个结论数字：
 *   1) 由 rAF 驱动，逐帧修改 scroll container 的 scrollTop（全量来回滚动），
 *      模拟真实滚轮的持续性输入，而不是「跳一次然后等」；
 *   2) 前 warmupMs 不计入统计（等虚拟化首帧、字体、图片稳定）；
 *   3) 统计 p50/p95/最差帧/掉帧数/长帧数，并顺带记录 DOM 里真实存在的 [data-row-index] 数量，
 *      用来证明「没有把 10,000 行真的渲染出来」。
 *
 * 三个入口都调用同一份实现，保证数字可比：
 *   - 页面按钮：/settings 或右下角性能面板的「运行滚动基准」
 *   - 浏览器控制台：await window.__AMS_PERF__.run(5000)
 *   - 无人值守：pnpm --filter @ams/web perf:scroll（Edge CDP 脚本）
 */

import { computeFrameReport, type FrameReport } from "./frameSampler.js";

export interface ScrollBenchOptions {
  readonly element: HTMLElement;
  readonly totalRows: number;
  readonly rowHeight: number;
  readonly durationMs?: number;
  readonly warmupMs?: number;
  /**
   * 每帧滚动的像素数。默认 48px（≈ 1.5 行 @32px 行高）——
   * 这是真实滚轮/触控板「快滑」的量级。
   *
   * 为什么不用「5 秒铺满 32 万像素」那种写法：那等于每帧瞬移 1000+ 像素，
   * 每帧都要把视口里所有行换掉，测出来的是「最坏换行压力」，不是用户实际滚动。
   * 想要压测换行压力，用 stepPx=1000 单独跑一轮（报告里会区分 flick / jump）。
   */
  readonly stepPx?: number;
}

export interface ScrollBenchResult extends FrameReport {
  readonly measuredAt: string;
  readonly totalRows: number;
  /** 测量期间 DOM 中真实存在的行节点数（虚拟化的直接证据） */
  readonly renderedRows: number;
  readonly scrollRangePx: number;
  readonly rowHeight: number;
  readonly viewportRows: number;
  /** 每帧滚动步长（px），用于区分「真实滑动」与「滚动条拖动」两种压力 */
  readonly stepPx: number;
  /** 完成几次「顶到底 / 底到顶」的往返 */
  readonly passes: number;
  readonly userAgent: string;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

export async function runScrollBench(options: ScrollBenchOptions): Promise<ScrollBenchResult> {
  const element = options.element;
  const durationMs = options.durationMs ?? 5000;
  const warmupMs = options.warmupMs ?? 600;

  const range = Math.max(0, element.scrollHeight - element.clientHeight);
  const viewportRows = Math.max(1, Math.round(element.clientHeight / Math.max(1, options.rowHeight)));
  element.scrollTop = 0;
  await nextFrame();
  await nextFrame();

  const deltas: number[] = [];
  let passes = 0;
  let lastTimestamp = 0;
  let direction = 1;
  const totalMs = warmupMs + durationMs;
  const stepPx = Math.max(1, options.stepPx ?? 48);

  await new Promise<void>((resolve) => {
    const start = performance.now();

    const tick = (timestamp: number): void => {
      const elapsed = timestamp - start;
      const measuring = elapsed >= warmupMs;
      if (lastTimestamp > 0 && measuring) {
        deltas.push(timestamp - lastTimestamp);
      }
      lastTimestamp = timestamp;

      let next = element.scrollTop + stepPx * direction;
      if (next >= range) {
        next = range;
        direction = -1;
        passes += 1;
      } else if (next <= 0) {
        next = 0;
        direction = 1;
        passes += 1;
      }
      element.scrollTop = next;

      if (elapsed >= totalMs) {
        resolve();
        return;
      }
      window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);
  });

  const report = computeFrameReport(deltas);
  const renderedRows = element.querySelectorAll("[data-row-index]").length;

  return {
    ...report,
    measuredAt: new Date().toISOString(),
    totalRows: options.totalRows,
    renderedRows,
    scrollRangePx: Math.round(range),
    rowHeight: options.rowHeight,
    viewportRows,
    stepPx,
    passes,
    userAgent: typeof navigator === "undefined" ? "unknown" : navigator.userAgent,
  };
}

export type ScrollBenchRunner = () => Promise<ScrollBenchResult>;

let runner: ScrollBenchRunner | null = null;
let latest: ScrollBenchResult | null = null;

/** 由广告表页面注册（只有它知道 scroll container 与行高） */
export function registerScrollBenchRunner(next: ScrollBenchRunner | null): void {
  runner = next;
}

export function getScrollBenchRunner(): ScrollBenchRunner | null {
  return runner;
}

export function setLatestBenchResult(result: ScrollBenchResult | null): void {
  latest = result;
}

export function getLatestBenchResult(): ScrollBenchResult | null {
  return latest;
}
